import React, { useEffect, useRef, useState } from 'react';
import { prop, propOr, equals, toLower, trim, includes, isNotEmpty } from 'ramda';
import truncateWithHighlight from '../../../../utils/truncateWithHighlight';
import HighlightText from './HighlightText';

const TruncatedHighlightText = ({ text, searchTerm, actionCellRef, locationRef, onTruncationChange }) => {
  const textRef = useRef(null);
  const safeText = text || '';
  const [renderState, setRenderState] = useState({
    visibleText: safeText,
    hasEllipsis: false,
    highlightEllipsis: false
  });

  useEffect(() => {
    const el = prop('current', textRef);
    if (!el) return undefined;

    const measureEl = prop('current', actionCellRef) || propOr(el, 'parentElement', el);
    let frameId = null;

    const recompute = () => {
      if (!safeText) {
        const nextHasEllipsis = false;
        setRenderState(prev => (prev.visibleText === '' && !prev.hasEllipsis && !prev.highlightEllipsis
          ? prev
          : {
            visibleText: '',
            hasEllipsis: false,
            highlightEllipsis: false
          }));
        if (onTruncationChange) onTruncationChange(nextHasEllipsis);
        return;
      }

      const style = window.getComputedStyle(el);
      const font = `${prop('fontWeight', style)} ${prop('fontSize', style)} ${prop('fontFamily', style)}`;
      const locationEl = prop('current', locationRef);
      const locationWidth = locationEl ? prop('width', locationEl.getBoundingClientRect()) : 0;
      const spacing = locationEl ? 4 : 0;
      const maxWidth = Math.max(0, prop('clientWidth', measureEl) - locationWidth - spacing);

      if (!maxWidth) {
        const nextHasEllipsis = false;
        setRenderState(prev => (prev.visibleText === safeText && !prev.hasEllipsis && !prev.highlightEllipsis
          ? prev
          : {
            visibleText: safeText,
            hasEllipsis: false,
            highlightEllipsis: false
          }));
        if (onTruncationChange) onTruncationChange(nextHasEllipsis);
        return;
      }

      const { truncated, wasTruncated } = truncateWithHighlight(safeText, maxWidth, font);
      const visibleText = wasTruncated && truncated.endsWith('…')
        ? truncated.slice(0, -1)
        : truncated;

      const normalizedSearchTerm = toLower(trim(searchTerm || ''));
      const hiddenText = wasTruncated ? safeText.slice(visibleText.length) : '';
      const normalizedHiddenText = toLower(hiddenText);
      const highlightEllipsis = wasTruncated && isNotEmpty(normalizedSearchTerm) && includes(normalizedSearchTerm, normalizedHiddenText);

      const nextHasEllipsis = wasTruncated;
      setRenderState(prev => {
        const nextState = {
          visibleText,
          hasEllipsis: wasTruncated,
          highlightEllipsis
        };
        return equals(prev, nextState) ? prev : nextState;
      });
      if (onTruncationChange) onTruncationChange(nextHasEllipsis);
    };

    const cancelScheduledRecompute = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }
    };

    const scheduleRecompute = () => {
      // Collapse multiple resize events into a single recompute per frame.
      cancelScheduledRecompute();
      frameId = window.requestAnimationFrame(recompute);
    };

    scheduleRecompute();
    window.addEventListener('resize', scheduleRecompute);

    return () => {
      cancelScheduledRecompute();
      window.removeEventListener('resize', scheduleRecompute);
    };
  }, [safeText, searchTerm, actionCellRef, locationRef, onTruncationChange]);

  return (
    <span className="min-w-0 flex items-center overflow-hidden">
      <HighlightText
        ref={textRef}
        text={propOr('', 'visibleText', renderState)}
        searchTerm={searchTerm}
        className="block min-w-0 truncate"
      />

      {prop('hasEllipsis', renderState) && (prop('highlightEllipsis', renderState)
        ? <mark className="bg-flexi-history-highlight p-0 shrink-0">…</mark>
        : <span className="shrink-0">…</span>)}
    </span>
  );
};

export default TruncatedHighlightText;
